import { Box, Button, Typography } from '@material-ui/core';
import { Skeleton } from '@material-ui/lab';
import OpenInNewOutlinedIcon from '@material-ui/icons/OpenInNewOutlined';
import CheckCircleIcon from '@material-ui/icons/CheckCircle';
import StopCircleOutlinedIcon from '@material-ui/icons/StopCircleOutlined';
import Assessment from '@material-ui/icons/Assessment';
import AssessmentIcon from '@material-ui/icons/Assessment';
import Description from '@material-ui/icons/Description';
import type { ExecutionDashboard, IqeProjectStatus } from '../../interfaces/ui';
import '../../styles/executionCenter.css';
import '../../styles/projectDetails.css';
import { WarningIcon } from '../../icons/WarningIcon';
import {
  isProjectOperationStopped,
  isProjectTerminalFailure,
} from '../../utils/projectStatusChecks';

interface ExecutionResultsTabProps {
  data: ExecutionDashboard | null;
  loading: boolean;
  error: string | null;
  projectId?: string;
  onViewDetailedResults?: (projectId: string) => void;
  projectStatus?: IqeProjectStatus;
  executionMode?: string;
  isStoppedOverride?: boolean;
}

export default function ExecutionResultsTab({
  data,
  loading,
  error,
  projectStatus,
  executionMode,
  isStoppedOverride = false,
}: ExecutionResultsTabProps) {
  const stoppedResultsState = (
    <Box className="pd-stopped-results-empty-state" role="status">
      <Box className="pd-stopped-results-icon" aria-hidden="true">
        <StopCircleOutlinedIcon />
      </Box>
      <Typography className="pd-stopped-results-title">Run stopped</Typography>
      <Typography className="pd-stopped-results-subtitle">
        {executionMode?.toLowerCase() === 'enhance' ||
        executionMode?.toLowerCase() === 'generate'
          ? 'The run was stopped. No new test cases were created and no results were generated.'
          : 'The execution run was stopped before results were generated.'}
      </Typography>
      <Typography className="pd-stopped-results-subtitle">
        Click on{' '}
        <b>
          {executionMode?.toLowerCase() === 'enhance'
            ? 'Enhance and Execute Test Plan'
            : executionMode?.toLowerCase() === 'execute'
            ? 'Execute Automation Suite'
            : 'Generate & Execute'}
        </b>{' '}
        {executionMode?.toLowerCase() === 'enhance'
          ? 'to retry enhancement.'
          : executionMode?.toLowerCase() === 'execute'
          ? 'to restart the execution.'
          : 'to retry generation and execution.'}
      </Typography>
    </Box>
  );

  const isStopped =
    isStoppedOverride ||
    (projectStatus ? isProjectOperationStopped(projectStatus) : false);

  if (isStopped) {
    return stoppedResultsState;
  }

  if (loading) {
    return (
      <Box
        className="ec-results-skeleton"
        aria-label="Loading execution results"
      >
        <Skeleton variant="text" className="ec-results-skeleton-status" />
        <Box className="ec-results-skeleton-stats">
          {[0, 1, 2, 3, 4].map(stat => (
            <Box key={stat} className="ec-results-skeleton-stat">
              <Skeleton
                variant="text"
                className="ec-results-skeleton-stat-label"
              />
              <Skeleton
                variant="text"
                className="ec-results-skeleton-stat-value"
              />
            </Box>
          ))}
        </Box>
        <Box className="ec-results-skeleton-actions">
          <Skeleton variant="rect" className="ec-results-skeleton-button" />
          <Skeleton variant="rect" className="ec-results-skeleton-button" />
        </Box>
      </Box>
    );
  }

  if (error && !isStopped) {
    return (
      <Box className="pd-empty-state">
        <Box className="pd-error-icon-box">
          <WarningIcon color="var(--iqe-error)" />
        </Box>
        <Typography className="pd-empty-title">Operation failed</Typography>
        <Typography className="pd-empty-subtitle">
          The operation failed on the agent side and the dashboard URL was not
          generated. Please retry the operation.
        </Typography>
      </Box>
    );
  }

  if (!data) {
    const isAgentFailed = projectStatus
      ? isProjectTerminalFailure(projectStatus)
      : false;
    if (isStopped) {
      return stoppedResultsState;
    }

    return (
      <Box className="pd-empty-state">
        <Box className="pd-empty-icon-box">
          <AssessmentIcon />
        </Box>
        <Typography className="pd-empty-title">No results yet</Typography>
        <Typography className="pd-empty-subtitle">
          {isAgentFailed
            ? 'The operation failed. Please retry the operation.'
            : 'Execution results will appear here once the run completes.'}
        </Typography>
      </Box>
    );
  }

  const dashboardUrl = data?.dashboardUrl?.trim();
  const reportUrl = data?.reportUrl?.trim();
  const normalizedStatus = data?.status?.trim().toUpperCase();
  const isFailedRun = normalizedStatus.includes('FAILED');
  const isStoppedRun = normalizedStatus.includes('STOPPED');
  const canViewSummaryReport =
    executionMode === 'generate' || executionMode === 'enhance';

  if (!dashboardUrl) {
    if (isStoppedRun) {
      return stoppedResultsState;
    }

    return (
      <Box className="pd-empty-state">
        <Box
          className={isFailedRun ? 'pd-error-icon-box' : 'pd-empty-icon-box'}
        >
          {isFailedRun ? (
            <WarningIcon color="var(--iqe-error)" />
          ) : (
            <AssessmentIcon />
          )}
        </Box>
        <Typography className="pd-empty-title">
          {isFailedRun ? 'Operation failed' : 'No results yet'}
        </Typography>
        <Typography className="pd-empty-subtitle">
          {isFailedRun
            ? 'The latest operation failed and the dashboard URL was not generated. Please retry the operation.'
            : 'Execution results will appear here once the run completes.'}
        </Typography>
      </Box>
    );
  }

  return (
    <Box className="ec-tab-panel">
      <Box className="ec-analysis-complete-banner">
        <Box className="ec-analysis-complete-icon-box">
          {isFailedRun ? (
            <WarningIcon color="var(--iqe-error)" />
          ) : (
            <CheckCircleIcon />
          )}
        </Box>
        <Box className="ec-analysis-complete-content">
          <Typography component="p" className="ec-analysis-complete-subtitle">
            {isFailedRun
              ? 'Test execution completed with failures.'
              : 'Test execution completed successfully.'}
          </Typography>
          <Typography
            component="p"
            className="ec-analysis-complete-description"
          >
            Detailed execution insights, screenshots, failed scenarios,
            recommendations, and execution evidence are available in the
            detailed results report.
          </Typography>
          <Box className="ec-analysis-banner-actions">
            <Button
              variant="outlined"
              color="primary"
              href={dashboardUrl}
              target="_blank"
              rel="noopener noreferrer"
              className="ec-btn-primary"
              size="medium"
              startIcon={<Assessment />}
              endIcon={
                <OpenInNewOutlinedIcon className="ec-open-in-new-icon" />
              }
            >
              View Detailed Results
            </Button>
            {canViewSummaryReport && reportUrl && (
              <Button
                variant="outlined"
                color="primary"
                href={reportUrl}
                target="_blank"
                rel="noopener noreferrer"
                className="ec-btn-primary"
                size="medium"
                startIcon={<Description />}
                endIcon={
                  <OpenInNewOutlinedIcon className="ec-open-in-new-icon" />
                }
              >
                View Summary Report
              </Button>
            )}
          </Box>
        </Box>
      </Box>
    </Box>
  );
}
